import Eventable from 'utils/eventable';
import type { Player } from '../../../../../types';
import type { IEngagementVideoAd } from '@schibsted/pulse-sdk';

interface AdMeta {
    adId: string;
    sequence: number;
    podcount: number;
    tag: string;
    duration: number;
    offset?: number | string;
    adschedule?: {
        offset: number | string;
    };
}

type AdSlotType = Exclude<
    Exclude<IEngagementVideoAd['object'], undefined>['adSlot'],
    undefined
>;

export interface AdData {
    id?: string;
    adSequenceDuration: number[];
    adFormat?: string;
    count?: number;
    sequence?: number;
    durationMs: number;
    type?: AdSlotType;
    positionMs: number;
    start: boolean;
}

function getAdSlotType(slot: string): AdSlotType | undefined {
    switch (slot.toLowerCase()) {
        case 'preroll':
            return 'Preroll';
        case 'midroll':
            return 'Midroll';
        case 'postroll':
            return 'Postroll';
        default:
            return undefined;
    }
}

/**
 * Parse SVP Player events and tailor data to pulse requirements
 *
 * Available events: AdStart, AdStop
 */
export class AdEvents extends Eventable {
    player: Player;
    adData: AdData;

    beforeUnload: [keyof WindowEventMap, (e: unknown) => void, boolean];

    constructor(player: Player) {
        super();

        this.onPlaylistItem = this.onPlaylistItem.bind(this);
        this.onAdSlotStart = this.onAdSlotStart.bind(this);
        this.onAdStarted = this.onAdStarted.bind(this);
        this.onAdProgress = this.onAdProgress.bind(this);
        this.onAdSlotComplete = this.onAdSlotComplete.bind(this);
        this.onAdFinished = this.onAdFinished.bind(this);
        this.onAdPlay = this.onAdPlay.bind(this);

        this.player = player;
        this.adData = {
            adSequenceDuration: [],
            start: false,
            durationMs: -1,
            positionMs: 0,
        };
        this.beforeUnload = [
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onAdSlotComplete,
            true,
        ];

        this.listenTo(this.player, 'playlistItem', this.onPlaylistItem);
        this.listenTo(this.player, 'adSlotStart', this.onAdSlotStart);
        this.listenTo(this.player, 'adStarted', this.onAdStarted);
        this.listenTo(this.player, 'adProgress', this.onAdProgress);
        this.listenTo(this.player, 'adSlotComplete', this.onAdSlotComplete);
        this.listenTo(this.player, 'adFinished', this.onAdFinished);
        this.listenTo(this.player, 'adPlay', this.onAdPlay);

        this.listenTo(this.player, 'playlistItem', () => {
            this.adData.adSequenceDuration = [];
        });

        window.addEventListener(...this.beforeUnload);
    }

    onPlaylistItem() {
        this.listenToOnce(this.player, 'complete', this.onAdSlotComplete);
    }

    onAdSlotStart(slotData: { meta: AdMeta; position: string }) {
        this.adData.type = getAdSlotType(slotData.position);
    }

    onAdProgress(time: number) {
        if (!this.adData.id) {
            return;
        }
        this.adData.positionMs = Math.floor(time) * 1000;
    }

    onAdSlotComplete() {
        this.sendStopEvent();

        this.adData = {
            adSequenceDuration: [],
            start: false,
            durationMs: -1,
            positionMs: 0,
        };

        window.removeEventListener(...this.beforeUnload);
    }

    onAdFinished() {
        this.sendStopEvent();
    }

    onAdStarted({ meta: adData }: jwplayer.AdStartedParam & { meta?: AdMeta }) {
        this.adData.start = true;

        // adData is missing from homad events
        if (!adData) {
            return;
        }

        const searchParams = new URLSearchParams(adData.tag.split('?').at(1));
        const adFormat =
            (searchParams.get('kw_no-sno-adformat') ??
                searchParams.get('no-sno-adformat')) ||
            undefined;

        this.adData.id = adData.adId;
        this.adData.sequence = adData.sequence;
        this.adData.count = adData.podcount;
        this.adData.positionMs = 0;
        this.adData.durationMs = adData.duration * 1000;
        if (adFormat) {
            this.adData.adFormat = adFormat;
        }

        if (!this.adData.adSequenceDuration.length) {
            this.adData.adSequenceDuration = Array(adData.podcount);
        }

        const adType = adData?.adschedule?.offset ?? adData.offset;
        // Preroll ads only
        if (adType === 'pre' && adData.duration) {
            this.adData.adSequenceDuration[adData.sequence - 1] =
                adData.duration;
        }
    }

    onAdPlay() {
        if (this.adData.start) {
            this.trigger('AdStart', this.adData);
        }
        // start indicates first play of ad
        this.adData.start = false;
    }

    sendStopEvent() {
        if (this.adData.id) {
            this.trigger('AdStop', this.adData);
            // keep adSequenceDuration for playback tracking purposes
            this.adData.id = undefined;
            this.adData.durationMs = -1;
            this.adData.start = false;
        }
    }

    destroy() {
        window.removeEventListener(...this.beforeUnload);
        this.stopListening();
    }
}
